import { useEffect, useState } from 'react'
import { Button, ErrorText, Modal, Spinner } from '../ui'
import { Icon } from '../ui/icons'
import { apiErrorMessage } from '../../services/api'
import { downloadDocument, fetchDocumentObjectUrl, type DocumentSource } from '../../services/documents'
import { fileSize, formatDate } from '../../utils/format'

export interface ViewableDocument {
  id: string
  title: string
  fileName: string
  mimeType: string
  sizeBytes: number
  examDate?: string | null
  notes?: string | null
}

/**
 * Visor de un documento clínico. El archivo se pide autenticado y se muestra
 * desde una URL en memoria (`blob:`) que muere al cerrar el visor: no queda
 * ninguna dirección del documento que se pueda compartir por accidente.
 */
export function DocumentViewer({
  document: doc,
  source,
  onClose,
}: {
  document: ViewableDocument
  source: DocumentSource
  onClose: () => void
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let revoked = false
    let url: string | null = null
    fetchDocumentObjectUrl(source, doc.id)
      .then((result) => {
        if (revoked) return URL.revokeObjectURL(result)
        url = result
        setObjectUrl(result)
      })
      .catch((err) => setError(apiErrorMessage(err)))
    return () => {
      revoked = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [doc.id, source])

  const isImage = doc.mimeType.startsWith('image/')

  return (
    <Modal title={doc.title} onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-xs text-gray-500">
          {doc.examDate ? `Examen del ${formatDate(doc.examDate)} · ` : ''}
          {doc.fileName} · {fileSize(doc.sizeBytes)}
        </p>
        {doc.notes && <p className="rounded-lg bg-soft px-3 py-2 text-sm text-steel-700">{doc.notes}</p>}
        <ErrorText>{error}</ErrorText>

        {!error &&
          (!objectUrl ? (
            <Spinner />
          ) : isImage ? (
            <img src={objectUrl} alt={doc.title} className="mx-auto max-h-[70vh] w-auto rounded-lg border border-gray-200" />
          ) : (
            <iframe src={objectUrl} title={doc.title} className="h-[70vh] w-full rounded-lg border border-gray-200" />
          ))}

        <div className="flex gap-2">
          <Button
            variant="cta"
            className="flex-1"
            onClick={() => downloadDocument(source, doc.id, doc.fileName).catch((err) => setError(apiErrorMessage(err)))}
          >
            <Icon name="download" className="mr-1.5 inline h-4 w-4" />
            Descargar
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  )
}
